import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Columns, Layers, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// In-app raster revision compare (NOT CAD-semantic). Side-by-side (desktop), opacity overlay,
// and a mobile A/B toggle. Lists change candidates between the two revisions for review.
const DrawingCompare = ({ projectId, revA, revB, isAdmin, onClose }) => {
  const [urlA, setUrlA] = useState(null);
  const [urlB, setUrlB] = useState(null);
  const [mode, setMode] = useState('side'); // side | overlay
  const [mobileRev, setMobileRev] = useState('A');
  const [opacity, setOpacity] = useState(0.5);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    let a, b;
    const fetchFile = async (id) => {
      const blob = await axios.get(`${API}/reference-drawings/${id}/file`, { responseType: 'blob' }).then((r) => r.data);
      return URL.createObjectURL(blob);
    };
    (async () => {
      try {
        [a, b] = await Promise.all([fetchFile(revA.id), fetchFile(revB.id)]);
        setUrlA(a); setUrlB(b);
        const dn = revA.drawingNumber || revB.drawingNumber;
        if (dn) axios.get(`${API}/projects/${projectId}/design/changes`, { params: { drawingNumber: dn } }).then((r) => setCandidates(r.data.items || [])).catch(() => {});
      } catch (e) { toast.error('Failed to load drawing files'); } finally { setLoading(false); }
    })();
    return () => { if (a) URL.revokeObjectURL(a); if (b) URL.revokeObjectURL(b); };
  }, [projectId, revA.id, revB.id]); // eslint-disable-line

  const reviewCandidate = async (cid, action) => {
    let description = null;
    if (action === 'EDIT') { description = prompt('Reviewed description:'); if (!description) return; }
    try { await axios.post(`${API}/projects/${projectId}/design/changes/${cid}/review`, { action, description }); toast.success('Change reviewed'); const dn = revA.drawingNumber || revB.drawingNumber; setCandidates((await axios.get(`${API}/projects/${projectId}/design/changes`, { params: { drawingNumber: dn } })).data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
  };

  const RevHeader = ({ d, label }) => (
    <div className="text-xs text-slate-600" data-testid={`compare-header-${label}`}>
      <b>{label}: {d.drawingNumber || d.fileName}</b> · {d.revision || '—'} · {d.revisionDate ? String(d.revisionDate).slice(0, 10) : 'no date'} · {d.status || 'DRAFT'}
    </div>
  );
  const Frame = ({ url }) => url ? <iframe title="rev" src={url} className="w-full h-full border-0 bg-white" /> : null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-slate-900/95" data-testid="drawing-compare">
      <div className="flex items-center justify-between px-4 py-3 bg-white">
        <div className="flex items-center gap-4 flex-wrap">
          <RevHeader d={revA} label="A" /><RevHeader d={revB} label="B" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMode('side')} className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 ${mode === 'side' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="compare-side-btn"><Columns className="w-3 h-3" /> Side-by-side</button>
          <button onClick={() => setMode('overlay')} className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 ${mode === 'overlay' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="compare-overlay-btn"><Layers className="w-3 h-3" /> Overlay</button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="compare-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-[11px] text-amber-800 flex items-center gap-2" data-testid="compare-alignment-note">
        <AlertTriangle className="w-3 h-3" /> Raster comparison for reviewer assistance — not CAD-semantic. If revisions differ in size/scale/rotation/crop, the overlay may not align; use side-by-side.
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 relative">
            {/* Mobile toggle */}
            <div className="md:hidden flex items-center gap-2 p-2 bg-white border-b">
              <button onClick={() => setMobileRev('A')} className={`px-3 py-1 text-xs rounded ${mobileRev === 'A' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`} data-testid="compare-mobile-a">Rev A</button>
              <button onClick={() => setMobileRev('B')} className={`px-3 py-1 text-xs rounded ${mobileRev === 'B' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`} data-testid="compare-mobile-b">Rev B</button>
            </div>
            {mode === 'side' ? (
              <>
                <div className="hidden md:flex h-full">
                  <div className="flex-1 h-full border-r border-slate-700"><Frame url={urlA} /></div>
                  <div className="flex-1 h-full"><Frame url={urlB} /></div>
                </div>
                <div className="md:hidden h-full"><Frame url={mobileRev === 'A' ? urlA : urlB} /></div>
              </>
            ) : (
              <div className="h-full relative" data-testid="compare-overlay-view">
                <div className="absolute inset-0"><Frame url={urlA} /></div>
                <div className="absolute inset-0" style={{ opacity }}><Frame url={urlB} /></div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-xs" data-testid="compare-opacity-control">
                  Rev A <input type="range" min="0" max="1" step="0.05" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} data-testid="compare-opacity-slider" /> Rev B
                </div>
              </div>
            )}
          </div>

          {/* Change candidates */}
          <div className="w-72 shrink-0 bg-white overflow-auto p-3 hidden lg:block" data-testid="compare-candidates">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Change Candidates</h4>
            {candidates.length === 0 ? <p className="text-xs text-slate-400">No change candidates recorded for this drawing.</p> : candidates.map((c) => (
              <div key={c.id} className="border border-slate-100 rounded-lg p-2 mb-2 text-xs" data-testid="compare-candidate-row">
                <p className="font-medium">{c.changeType} <span className="text-slate-400">({c.reviewStatus.replace(/_/g, ' ')})</span></p>
                {c.aiSummary && <p className="text-slate-500 mt-0.5">{c.aiSummary}</p>}
                {isAdmin && c.reviewStatus === 'PENDING' && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {['ACCEPT', 'EDIT', 'DISMISS', 'INVESTIGATE'].map((a) => <button key={a} onClick={() => reviewCandidate(c.id, a)} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-700 text-white" data-testid={`compare-${a.toLowerCase()}-btn`}>{a}</button>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingCompare;
